"use client";

import Image from "next/image";
import { useState, useCallback } from "react";

export default function Home() {
  const [modalOpen, setModalOpen] = useState<null | "admin" | "contractor">(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [userId, setUserId] = useState<number | null>(null);
  const [isContractorPassword, setIsContractorPassword] = useState(false);
  const [contractorDocumentoForPassword, setContractorDocumentoForPassword] = useState("");
  const [isContractorPasswordByDocument, setIsContractorPasswordByDocument] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Contratistas: login por documento (paso 1 = documento, paso 2 = clave)
  const [contractorStep, setContractorStep] = useState<1 | 2>(1);
  const [contractorDocumento, setContractorDocumento] = useState("");
  const [contractorNombre, setContractorNombre] = useState("");

  const showError = useCallback((message: string) => {
    setErrorMessage(message);
    setShowErrorModal(true);
  }, []);

  const openModal = useCallback((type: "admin" | "contractor") => {
    setEmail("");
    setPassword("");
    setContractorStep(1);
    setContractorDocumento("");
    setContractorNombre("");
    setModalOpen(type);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(null);
    setSubmitting(false);
  }, []);

  const handleContractorByDocument = useCallback(async () => {
    const doc = contractorDocumento.trim();
    if (!doc) {
      showError("Ingrese su documento");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`/api/auth/contractor-by-document?documento=${encodeURIComponent(doc)}`);
      const result = await res.json();
      if (result.success && result.data) {
        const { hasClave } = result.data;
        if (hasClave === false) {
          // No tiene clave en x_claves: pedir que cree la contraseña
          setContractorDocumentoForPassword(doc);
          setIsContractorPasswordByDocument(true);
          setShowPasswordModal(true);
          setModalOpen(null);
        } else {
          setContractorNombre(result.data.nombre);
          setContractorStep(2);
          setPassword("");
        }
      } else {
        showError(result.error || "No se encontró un contratista con ese documento");
      }
    } catch {
      showError("Error de conexión");
    } finally {
      setSubmitting(false);
    }
  }, [contractorDocumento, showError]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!modalOpen) return;

      if (modalOpen === "admin") {
        setSubmitting(true);
        try {
          const res = await fetch("/api/auth/login", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
          });
          const result = await res.json();
          if (result.success) {
            const d = result.data;
            localStorage.setItem("user", JSON.stringify(d));
            const pid = d?.id_proyecto;
            const n = pid != null && pid !== "" ? Number(pid) : NaN;
            if (Number.isFinite(n) && n > 0) {
              localStorage.setItem("admin_id_proyecto", String(n));
            } else {
              localStorage.removeItem("admin_id_proyecto");
            }
            window.location.href = "/admin";
            return;
          }
          if (result.needsPassword) {
            setUserId(result.userId);
            setIsContractorPassword(false);
            setIsContractorPasswordByDocument(false);
            setShowPasswordModal(true);
            setModalOpen(null);
          } else {
            showError(result.error || "Credenciales inválidas");
          }
        } catch {
          showError("Error de conexión");
        } finally {
          setSubmitting(false);
        }
        return;
      }

      // Contratistas: login por documento + clave
      if (modalOpen === "contractor") {
        if (contractorStep === 1) {
          handleContractorByDocument();
          return;
        }
        setSubmitting(true);
        try {
          const res = await fetch("/api/auth/login-contractor", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ documento: contractorDocumento.trim(), password }),
          });
          const result = await res.json();
          if (result.success) {
            localStorage.setItem("contractor", JSON.stringify(result.data));
            window.location.href = "/contratistas";
            return;
          }
          if (result.needsPassword && result.documento) {
            setContractorDocumentoForPassword(result.documento);
            setIsContractorPasswordByDocument(true);
            setShowPasswordModal(true);
            setModalOpen(null);
          } else {
            showError(result.error || "Credenciales inválidas");
          }
        } catch {
          showError("Error de conexión");
        } finally {
          setSubmitting(false);
        }
      }
    },
    [modalOpen, email, password, contractorStep, contractorDocumento, showError, handleContractorByDocument]
  );

  const handleSetPassword = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const byDocument = isContractorPasswordByDocument && contractorDocumentoForPassword.trim();
      if (!userId && !byDocument) return;
      if (newPassword !== confirmPassword) {
        showError("Las contraseñas no coinciden");
        return;
      }
      if (newPassword.length < 6) {
        showError("La contraseña debe tener al menos 6 caracteres");
        return;
      }
      setSubmitting(true);
      try {
        if (byDocument) {
          const res = await fetch("/api/auth/password-contractor-by-document", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              documento: contractorDocumentoForPassword.trim(),
              password: newPassword,
              confirmPassword,
            }),
          });
          const result = await res.json();
          if (result.success) {
            setShowPasswordModal(false);
            setNewPassword("");
            setConfirmPassword("");
            setContractorDocumentoForPassword("");
            setIsContractorPasswordByDocument(false);
            if (result.data) {
              localStorage.setItem("contractor", JSON.stringify(result.data));
              window.location.href = "/contratistas";
              return;
            }
          }
          showError(result.error || "Error al configurar la contraseña");
        } else {
          const endpoint = isContractorPassword ? "/api/auth/password-contractor" : "/api/auth/password";
          const res = await fetch(endpoint, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ userId, password: newPassword, confirmPassword }),
          });
          const result = await res.json();
          if (result.success) {
            setShowPasswordModal(false);
            setNewPassword("");
            setConfirmPassword("");
            setUserId(null);
            if (!isContractorPassword && result.data) {
              localStorage.setItem("user", JSON.stringify(result.data));
              const pid = result.data.id_proyecto;
              const n = pid != null && pid !== "" ? Number(pid) : NaN;
              if (Number.isFinite(n) && n > 0) {
                localStorage.setItem("admin_id_proyecto", String(n));
              } else {
                localStorage.removeItem("admin_id_proyecto");
              }
            }
            window.location.href = isContractorPassword ? "/contratistas" : "/admin";
            return;
          }
          showError(result.error || "Error al configurar la contraseña");
        }
      } catch {
        showError("Error de conexión");
      } finally {
        setSubmitting(false);
      }
    },
    [userId, newPassword, confirmPassword, isContractorPassword, isContractorPasswordByDocument, contractorDocumentoForPassword, showError]
  );

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900 antialiased dark:bg-zinc-950 dark:text-zinc-100 flex flex-col">
      <div className="flex flex-1 min-h-0 max-w-[1920px] mx-auto w-full flex-col lg:flex-row">
        {/* Izquierda: 75% — Solo visible en tablet (md) y escritorio; en celular se oculta */}
        <main className="hidden md:flex w-full lg:w-[75%] min-w-0 flex-col justify-center px-6 py-8 sm:px-8 sm:py-10 lg:px-12 lg:py-14">
          <Image
            src="/soco.png"
            alt="Soco"
            width={180}
            height={80}
            className="mb-6 object-contain"
            priority
          />
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl text-zinc-900 dark:text-white">
            Software de contratación por proyectos
          </h1>
          <p className="mt-4 text-lg text-zinc-600 dark:text-zinc-300 max-w-xl">
            Gestiona convocatorias, contratistas y seguimiento contractual en una sola plataforma.
            Acceso seguro para administradores y contratistas.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-sm text-emerald-800 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200">
              Transparencia
            </span>
            <span className="inline-flex items-center rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-sm text-sky-800 dark:border-sky-800 dark:bg-sky-900/30 dark:text-sky-200">
              Agilidad
            </span>
            <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
              Talento
            </span>
          </div>
        </main>

        {/* Derecha: 25% — Panel de ingreso; en celular ocupa toda la pantalla */}
        <aside className="w-full lg:w-[25%] lg:min-w-[280px] min-h-[50vh] md:min-h-0 flex-shrink-0 border-t lg:border-t-0 lg:border-l border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 flex flex-col justify-center p-6 sm:p-8 lg:p-10">
          <div className="w-full max-w-[320px] mx-auto flex flex-col items-stretch">
            <h2 className="text-xl font-semibold text-zinc-800 dark:text-zinc-100 mb-8 text-center lg:text-left">
              Acceso al portal
            </h2>
            <div className="flex flex-col gap-4">
              <button
                type="button"
                onClick={() => openModal("admin")}
                className="w-full flex items-center justify-center gap-4 rounded-xl border-2 border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 px-5 py-4 text-sm font-semibold text-emerald-800 dark:text-emerald-200 transition hover:border-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 min-h-[52px]"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-200/80 dark:bg-emerald-800/50">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
                    <path fillRule="evenodd" d="M4.5 6.375a4.125 4.125 0 1 1 8.25 0 4.125 4.125 0 0 1-8.25 0ZM14.25 8.625a3.375 3.375 0 1 1 6.75 0 3.375 3.375 0 0 1-6.75 0ZM1.5 19.125a7.125 7.125 0 0 1 14.25 0v.003l-.001.119a.75.75 0 0 1-.363.63 13.067 13.067 0 0 1-6.761 1.873c-2.472 0-4.786-.684-6.76-1.873a.75.75 0 0 1-.364-.63l-.001-.122ZM17.25 19.128l-.001.144a2.25 2.25 0 0 1-.233.96 10.088 10.088 0 0 0 5.06-1.01.75.75 0 0 0 .42-.643 4.875 4.875 0 0 0-6.957-4.611 8.586 8.586 0 0 1 1.71 5.157v.003Z" clipRule="evenodd" />
                  </svg>
                </span>
                Administradores
              </button>
              <button
                type="button"
                onClick={() => openModal("contractor")}
                className="w-full flex items-center justify-center gap-4 rounded-xl border-2 border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-900/20 px-5 py-4 text-sm font-semibold text-sky-800 dark:text-sky-200 transition hover:border-sky-400 hover:bg-sky-100 dark:hover:bg-sky-900/40 min-h-[52px]"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sky-200/80 dark:bg-sky-800/50">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
                    <path fillRule="evenodd" d="M8.25 6.75a3.75 3.75 0 1 1 7.5 0 3.75 3.75 0 0 1-7.5 0ZM15.75 9.75a3 3 0 1 1 6 0 3 3 0 0 1-6 0ZM2.25 9.75a3 3 0 1 1 6 0 3 3 0 0 1-6 0ZM6.31 15.117A6.745 6.745 0 0 1 12 12a6.745 6.745 0 0 1 6.709 3.098.75.75 0 0 1-.372.568A12.696 12.696 0 0 1 12 15.75c-2.305 0-4.47-.612-6.337-1.684a.75.75 0 0 1-.372-.568ZM6 18a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" clipRule="evenodd" />
                  </svg>
                </span>
                Contratistas
              </button>
            </div>
            <p className="mt-6 text-xs leading-relaxed text-zinc-500 dark:text-zinc-400 text-center lg:text-left">
              Administradores: correo y contraseña. Contratistas: documento y clave.
            </p>
          </div>
        </aside>
      </div>

      {/* Modal de login */}
      {modalOpen && (
        <div className="fixed inset-0 z-30 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={closeModal} aria-hidden />
          <div className="relative z-10 w-full max-w-md rounded-2xl border border-zinc-200 bg-white p-6 shadow-xl dark:border-zinc-700 dark:bg-zinc-900">
            <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
              {modalOpen === "admin" ? "Ingreso Administradores" : "Ingreso Contratistas"}
            </h3>
            <form className="mt-4 space-y-4" onSubmit={handleSubmit}>
              {modalOpen === "admin" ? (
                <>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">Correo</label>
                    <input
                      id="email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="usuario@correo.com"
                      className="mt-1 w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm outline-none focus:border-emerald-500 dark:border-zinc-600 dark:bg-zinc-800 dark:focus:border-emerald-400"
                      autoComplete="email"
                    />
                  </div>
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">Contraseña</label>
                    <div className="relative mt-1">
                      <input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••"
                        className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 pr-10 text-sm outline-none focus:border-emerald-500 dark:border-zinc-600 dark:bg-zinc-800 dark:focus:border-emerald-400"
                        autoComplete="current-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((s) => !s)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
                        aria-label={showPassword ? "Ocultar contraseña" : "Ver contraseña"}
                      >
                        {showPassword ? (
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {contractorStep === 1 ? (
                    <div>
                      <label htmlFor="documento" className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">Documento</label>
                      <input
                        id="documento"
                        type="text"
                        value={contractorDocumento}
                        onChange={(e) => setContractorDocumento(e.target.value)}
                        placeholder="Número de documento"
                        className="mt-1 w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm outline-none focus:border-sky-500 dark:border-zinc-600 dark:bg-zinc-800 dark:focus:border-sky-400"
                        autoComplete="off"
                      />
                    </div>
                  ) : (
                    <>
                      <div className="rounded-lg border border-zinc-200 bg-zinc-50 dark:border-zinc-600 dark:bg-zinc-800 px-3 py-2">
                        <p className="text-xs font-medium text-zinc-500 dark:text-zinc-400">Contratista</p>
                        <p className="text-sm font-medium text-zinc-900 dark:text-white">{contractorNombre || "—"}</p>
                      </div>
                      <div>
                        <label htmlFor="password" className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">Clave</label>
                        <div className="relative mt-1">
                          <input
                            id="password"
                            type={showPassword ? "text" : "password"}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="••••••"
                            required={contractorStep === 2}
                            className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 pr-10 text-sm outline-none focus:border-sky-500 dark:border-zinc-600 dark:bg-zinc-800 dark:focus:border-sky-400"
                            autoComplete="current-password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword((s) => !s)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
                            aria-label={showPassword ? "Ocultar contraseña" : "Ver contraseña"}
                          >
                            {showPassword ? (
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
                              </svg>
                            ) : (
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
              <div className="flex gap-2">
                {modalOpen === "contractor" && contractorStep === 2 && (
                  <button
                    type="button"
                    onClick={() => { setContractorStep(1); setContractorNombre(""); setPassword(""); }}
                    className="rounded-lg border border-zinc-300 px-4 py-2.5 text-sm font-medium text-zinc-700 transition hover:bg-zinc-50 dark:border-zinc-600 dark:text-zinc-300 dark:hover:bg-zinc-800"
                  >
                    Atrás
                  </button>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  className={`flex-1 rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition disabled:opacity-60 ${modalOpen === "contractor" ? "bg-sky-600 hover:bg-sky-700" : "bg-emerald-600 hover:bg-emerald-700"}`}
                >
                  {submitting ? "…" : modalOpen === "contractor" && contractorStep === 1 ? "Continuar" : "Ingresar"}
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  className="rounded-lg border border-zinc-300 px-4 py-2.5 text-sm font-medium text-zinc-700 transition hover:bg-zinc-50 dark:border-zinc-600 dark:text-zinc-300 dark:hover:bg-zinc-800"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal configurar contraseña */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-30 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowPasswordModal(false)} aria-hidden />
          <div className="relative z-10 w-full max-w-md rounded-2xl border border-zinc-200 bg-white p-6 shadow-xl dark:border-zinc-700 dark:bg-zinc-900">
            <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">Configurar contraseña</h3>
            <form className="mt-4 space-y-4" onSubmit={handleSetPassword}>
              <div>
                <label htmlFor="new-pwd" className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">Nueva contraseña</label>
                <div className="relative mt-1">
                  <input
                    id="new-pwd"
                    type={showNewPassword ? "text" : "password"}
                    required
                    minLength={6}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="••••••"
                    className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 pr-10 text-sm outline-none focus:border-emerald-500 dark:border-zinc-600 dark:bg-zinc-800"
                    autoComplete="new-password"
                  />
                  <button type="button" onClick={() => setShowNewPassword((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500">
                    {showNewPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              <div>
                <label htmlFor="confirm-pwd" className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">Confirmar contraseña</label>
                <div className="relative mt-1">
                  <input
                    id="confirm-pwd"
                    type={showConfirmPassword ? "text" : "password"}
                    required
                    minLength={6}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••"
                    className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 pr-10 text-sm outline-none focus:border-emerald-500 dark:border-zinc-600 dark:bg-zinc-800"
                    autoComplete="new-password"
                  />
                  <button type="button" onClick={() => setShowConfirmPassword((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500">
                    {showConfirmPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              <p className="text-xs text-zinc-500 dark:text-zinc-400">Mínimo 6 caracteres.</p>
              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-60"
              >
                {submitting ? "Guardando…" : "Guardar contraseña"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Modal error */}
      {showErrorModal && (
        <div className="fixed inset-0 z-30 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => { setShowErrorModal(false); setErrorMessage(""); }} aria-hidden />
          <div className="relative z-10 w-full max-w-md rounded-2xl border border-zinc-200 bg-white p-6 shadow-xl dark:border-zinc-700 dark:bg-zinc-900">
            <div className="flex items-center gap-3 mb-4">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5 text-red-600 dark:text-red-400">
                  <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25Zm-1.72 6.97a.75.75 0 1 0-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 1 0 1.06 1.06L12 13.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L13.06 12l1.72-1.72a.75.75 0 1 0-1.06-1.06L12 10.94l-1.72-1.72Z" clipRule="evenodd" />
                </svg>
              </span>
              <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">Error</h3>
            </div>
            <p className="text-sm text-zinc-600 dark:text-zinc-300 mb-4">{errorMessage}</p>
            <button
              onClick={() => { setShowErrorModal(false); setErrorMessage(""); }}
              className="w-full rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700"
            >
              Aceptar
            </button>
          </div>
        </div>
      )}

      <footer className="border-t border-zinc-200 bg-white py-4 text-center text-sm text-zinc-500 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-400">
        SOCO – Software de contratación por proyectos. By FONPACIFICO © Todos los derechos reservados.
      </footer>
    </div>
  );
}
